import { Link } from "react-router-dom";
import { useEffect } from "react";
import "./landing.css";

export function LandingPage() {
  useEffect(() => {
    document.body.classList.add("page-landing");
    return () => document.body.classList.remove("page-landing");
  }, []);

  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="landing-logo">
          Simple <em>Cash Discount</em>
        </div>
        <Link to="/login" className="landing-cta-btn">
          Get Started
        </Link>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-badge">
          <span className="landing-badge-dot" />
          Official Square Integration
        </div>
        <h1>
          The right way to run a <em>cash discount</em> program
        </h1>
        <p className="landing-hero-sub">
          True cash discount pricing built directly into Square. Customers pay the
          right price — every time — with tax calculated correctly.
        </p>
        <div>
          <Link to="/login" className="landing-btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="2" y="2" width="20" height="20" rx="3" />
            </svg>
            Connect with Square — It's Free
          </Link>
          <p className="landing-hero-note">
            No credit card required · Works with any Square account
          </p>
        </div>
      </section>

      {/* Logos */}
      <div className="landing-logos">
        <p>Works with all Square devices</p>
        <div className="landing-logos-row">
          {["Square Terminal", "Square Register", "Square POS", "Square Handheld"].map(
            (name) => (
              <div key={name} className="landing-logo-badge">
                {name}
              </div>
            ),
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="landing-section">
        <p className="landing-section-label">How it works</p>
        <h2 className="landing-section-title">
          Set up in minutes.
          <br />
          Run it forever.
        </h2>
        <p className="landing-section-sub">
          Connect your Square account, choose your mode, and Simple Cash Discount
          handles the rest automatically.
        </p>

        <div className="landing-steps">
          {[
            { num: "01", title: "Connect Square", desc: "One click connects your Square account. No manual setup, no API keys to manage." },
            { num: "02", title: "Choose your mode", desc: "Mode 1 adjusts prices at checkout. Mode 2 inflates your catalog and discounts for cash. Switch anytime." },
            { num: "03", title: "Ring it up", desc: "Add items to your cart. Hit Charge. Cash and card prices appear side by side — merchant or customer picks." },
            { num: "04", title: "Tax is always right", desc: "Tax is calculated on the actual charged price — not reversed from an inflated number. No penny-off errors." },
          ].map((step) => (
            <div key={step.num} className="landing-step">
              <div className="landing-step-num">{step.num}</div>
              <div className="landing-step-title">{step.title}</div>
              <div className="landing-step-desc">{step.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="landing-divider" />

      {/* Two Modes */}
      <div className="landing-section">
        <p className="landing-section-label">Two modes</p>
        <h2 className="landing-section-title">
          Built for how your business actually works
        </h2>
        <p className="landing-section-sub">
          Most merchants want one and stick with it. You can switch anytime.
        </p>

        <div className="landing-modes">
          <div className="landing-mode-card featured">
            <div className="landing-mode-tag m1">Mode 1</div>
            <div className="landing-mode-title">True Cash Discount</div>
            <div className="landing-mode-desc">
              Your menu prices stay as-is. When a customer pays by card, each line item
              is recalculated using the correct formula before the order is sent to
              Square. Tax applies after.
            </div>
            <div className="landing-mode-detail">
              Card price = <strong>P / (1 - fee%)</strong>
              <br />
              Cash price = <strong>original menu price</strong>
              <br />
              Tax on = <strong>actual charged amount</strong>
            </div>
          </div>
          <div className="landing-mode-card">
            <div className="landing-mode-tag m2">Mode 2</div>
            <div className="landing-mode-title">Inflated Inventory</div>
            <div className="landing-mode-desc">
              Raise your entire Square catalog to the card price once. Cash customers
              get a discount applied at checkout. Perfect for bars and restaurants that
              want clean menu prices.
            </div>
            <div className="landing-mode-detail">
              Inflated = <strong>P / (1 - fee%)</strong>
              <br />
              Cash restore = <strong>locked original price</strong>
              <br />
              Optional = <strong>round up to nearest $1</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="landing-divider" />

      {/* Features */}
      <div className="landing-section">
        <p className="landing-section-label">Features</p>
        <h2 className="landing-section-title">
          Everything you need.
          <br />
          Nothing you don't.
        </h2>

        <div className="landing-features">
          {[
            { icon: "gold", emoji: "\uD83D\uDCB5", title: "Cash/card modal at checkout", desc: "Both prices appear side by side the moment you hit Charge. Merchant or customer picks — your choice in settings." },
            { icon: "green", emoji: "\uD83E\uDDFE", title: "Transparent receipts", desc: "Card receipts show the base price and service fee separately. Cash receipts show how much the customer saved. All toggleable." },
            { icon: "blue", emoji: "\uD83D\uDCC4", title: "Bill pricing", desc: "Print bills from your Square receipt printer showing both cash and card prices per item — before the customer decides how to pay." },
            { icon: "gold", emoji: "\uD83D\uDCE6", title: "Smart inventory tools", desc: "Inflate your entire Square catalog in one tap. Preview every price change before committing. Restore to exact original prices anytime — no math drift." },
            { icon: "green", emoji: "\uD83D\uDCE1", title: "Square Terminal support", desc: "Pair your Square Terminal and send card payments directly. Customer sees the amount on the Terminal screen and taps or inserts their card." },
            { icon: "blue", emoji: "\uD83D\uDD12", title: "Price locking", desc: "Original prices are locked the first time you inflate. No matter how many times you inflate or what rounding you use, restore always goes back to the exact original." },
          ].map((f) => (
            <div key={f.title} className="landing-feature">
              <div className={`landing-feature-icon ${f.icon}`}>{f.emoji}</div>
              <div className="landing-feature-title">{f.title}</div>
              <div className="landing-feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="landing-divider" />

      {/* Pricing */}
      <div className="landing-section">
        <p className="landing-section-label">Pricing</p>
        <h2 className="landing-section-title">
          Simple pricing.
          <br />
          Like everything else here.
        </h2>

        <div className="landing-pricing">
          <div className="landing-pricing-tag">Free during beta</div>
          <div className="landing-pricing-price">
            <sup>$</sup>0
          </div>
          <div className="landing-pricing-period">
            per month · no credit card required
          </div>
          <ul className="landing-pricing-features">
            <li>Unlimited transactions</li>
            <li>All Square devices supported</li>
            <li>Both Mode 1 and Mode 2</li>
            <li>Square Terminal pairing</li>
            <li>Full receipt &amp; bill customization</li>
            <li>Multi-location support coming soon</li>
          </ul>
          <Link to="/login" className="landing-btn-primary" style={{ margin: "0 auto" }}>
            Get started free
          </Link>
        </div>
      </div>

      {/* CTA */}
      <div className="landing-cta-section">
        <div className="landing-cta-box">
          <h2>Ready to start your cash discount program?</h2>
          <p>
            Connect your Square account in one click. Works with your existing catalog
            and Terminal.
          </p>
          <Link to="/login" className="landing-btn-primary" style={{ margin: "0 auto" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="2" y="2" width="20" height="20" rx="3" />
            </svg>
            Connect with Square
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-logo">
          Simple <em>Cash Discount</em>
        </div>
        <div className="landing-footer-links">
          <Link to="/login">Get Started</Link>
          <a href="mailto:support@simplecashdiscount.com">Support</a>
        </div>
        <div className="landing-footer-copy">
          &copy; 2026 Simple Cash Discount. Not affiliated with Square, Inc.
        </div>
      </footer>
    </div>
  );
}
